import { useMemo } from "react";
import { DirectorySort } from "@common/constants/counterparty";
import { RegisteredCounterpartyIncludeScope } from "@common/helpers/counterparties";
import type { Counterparty } from "@common/types/counterparty";
import { SEARCH_DEBOUNCE_TIME } from "@constants";
import { useDebouncedValue } from "@hooks/useDebouncedValue";
import { useCounterpartyPickerQuery } from "@api/counterparty.api";
import { CounterpartyGlyph } from "./CounterpartyGlyph";
import {
    MAX_ENTITY_REF_OPTIONS,
    NO_ENTITY_REF_OPTIONS,
    type EntityRefOption,
    type EntityRefOptionsParams,
    type EntityRefOptionsResult,
} from "./entityRefOptions.types";

const FIVE_MINUTES = 5 * 60 * 1000;

function toCounterpartyOption(counterparty: Counterparty): EntityRefOption {
    return {
        key: `counterparty:${counterparty.id}`,
        entityRef: {
            kind: "counterparty",
            id: counterparty.id,
            label: counterparty.name,
        },
        icon: <CounterpartyGlyph />,
        title: counterparty.name,
        description: counterparty.email ?? undefined,
    };
}

export function useCounterpartyRefOptions({
    query,
    isEnabled,
}: EntityRefOptionsParams): EntityRefOptionsResult {
    const typedSearch = query.trim();
    const search = useDebouncedValue(typedSearch, SEARCH_DEBOUNCE_TIME);

    const counterpartiesQuery = useCounterpartyPickerQuery(
        {
            search: search || undefined,
            sort: DirectorySort.NAME_ASC,
            includeRegistered: search
                ? RegisteredCounterpartyIncludeScope.ALL
                : RegisteredCounterpartyIncludeScope.USED,
        },
        {
            enabled: isEnabled,
            staleTime: search ? undefined : FIVE_MINUTES,
        },
    );

    const counterparties = counterpartiesQuery.data;
    const isSearchPending = typedSearch !== search;
    const isLoading = counterpartiesQuery.isLoading;

    return useMemo(() => {
        if (!isEnabled) {
            return NO_ENTITY_REF_OPTIONS;
        }
        if (isSearchPending) {
            return { options: [], isLoading: true };
        }
        const options = (counterparties ?? [])
            .slice(0, MAX_ENTITY_REF_OPTIONS)
            .map(toCounterpartyOption);
        return { options, isLoading };
    }, [isEnabled, isSearchPending, counterparties, isLoading]);
}
